/**
 * Opens `base` (another KoolFood app, e.g. the accountant portal) in a new tab
 * and hands off the current session token via `postMessage` instead of a URL
 * query param — a token in the URL ends up in browser history, server access
 * logs, and Referer headers; postMessage never touches any of those.
 */
export function handoffToken(base: string, token: string | null) {
    const targetOrigin = new URL(base).origin;
    const popup = window.open(base, '_blank');
    if (!popup || !token) return;

    const onMessage = (e: MessageEvent) => {
        if (e.origin !== targetOrigin || e.data?.type !== 'handoff-ready') return;
        popup.postMessage({ type: 'handoff-token', token }, targetOrigin);
        cleanup();
    };
    const cleanup = () => {
        window.removeEventListener('message', onMessage);
        clearTimeout(timer);
    };
    window.addEventListener('message', onMessage);
    const timer = setTimeout(cleanup, 10_000);
}

/**
 * Call once on mount in an app that can be opened via `handoffToken`. Announces
 * readiness to the opener and stores the token it sends back, if any.
 */
export function receiveTokenHandoff(onToken: (token: string) => void): () => void {
    if (!window.opener) return () => {};

    const onMessage = (e: MessageEvent) => {
        if (e.data?.type !== 'handoff-token' || !e.data.token) return;
        onToken(e.data.token);
    };
    window.addEventListener('message', onMessage);
    window.opener.postMessage({ type: 'handoff-ready' }, '*');
    return () => window.removeEventListener('message', onMessage);
}
